import type { Location, Customer, Project, Site, Area, LocationHierarchy } from '../types/locations'

/**
 * Extract location type from colon notation in description
 * e.g., ":customer: Acme Corp" -> "customer"
 */
export function extractColonType(description?: string): string | null {
  if (!description) return null
  const match = description.match(/^:(\w+):/)
  return match ? match[1] : null
}

/**
 * Remove colon notation from description for display
 * e.g., ":customer: Acme Corp" -> "Acme Corp"
 */
export function getDisplayDescription(description?: string): string {
  if (!description) return ''
  return description.replace(/^:\w+:\s*/, '')
}

/**
 * Build hierarchical location structure from flat array
 * Uses colon notation and parentLocIds to construct hierarchy:
 * Customer (no parents) → Site/Project → Project/Site → Area
 */
export function buildLocationHierarchy(locations: Location[]): LocationHierarchy {
  const customers: Customer[] = []
  const sites: Site[] = []
  const projects: Project[] = []
  const areas: Area[] = []

  // Create location lookup for efficient parent resolution
  const locationMap = new Map(locations.map(loc => [loc.locationId, loc]))

  // Helper to extract type from colon notation
  const extractLocationTypeFromDescription = (description?: string): string | null => {
    if (!description) return null
    const match = description.match(/^:(\w+):/)
    return match ? match[1].toLowerCase() : null
  }

  // Helper to determine location type and depth
  const getLocationTypeInfo = (location: Location): { type: string, depth: number } => {
    // No parents = customer (top-level)
    if (!location.parentLocIds || location.parentLocIds.length === 0) {
      return { type: 'customer', depth: 0 }
    }

    // Extract from colon notation (most reliable)
    const extractedType = extractLocationTypeFromDescription(location.description)
    if (extractedType) {
      // Calculate depth by counting parent chain
      let depth = 0
      let currentParentId: string | undefined = location.parentLocIds?.[0]
      while (currentParentId) {
        depth++
        const parentLocation = locationMap.get(currentParentId)
        if (!parentLocation) break
        currentParentId = parentLocation.parentLocIds?.[0]
      }
      return { type: extractedType, depth }
    }

    // Fallback: determine by depth in hierarchy
    const parentId = location.parentLocIds[0]
    const parent = locationMap.get(parentId)
    if (!parent) {
      // Parent not found, assume depth 1
      return { type: 'site', depth: 1 }
    }

    const parentTypeInfo = getLocationTypeInfo(parent)
    if (parentTypeInfo.type === 'customer') {
      return { type: 'site', depth: 1 }
    } else if (parentTypeInfo.type === 'site' || parentTypeInfo.type === 'project') {
      return { type: 'area', depth: parentTypeInfo.depth + 1 }
    }

    return { type: 'area', depth: 2 }
  }

  // Classify locations by type (create fresh copies to avoid mutation)
  for (const location of locations) {
    const { type } = getLocationTypeInfo(location)

    if (type === 'customer') {
      customers.push({ ...location } as Customer)
    } else if (type === 'site') {
      sites.push({ ...location } as Site)
    } else if (type === 'project') {
      projects.push({ ...location } as Project)
    } else if (type === 'area') {
      areas.push({ ...location } as Area)
    }
  }

  // Link children to parents

  // Link sites and projects to customers
  for (const site of sites) {
    if (site.parentLocIds && site.parentLocIds.length > 0) {
      const parentId = site.parentLocIds[0]
      const parent = customers.find(c => c.locationId === parentId)
      if (parent) {
        if (!parent.sites) parent.sites = []
        parent.sites.push(site)
        site.parentId = parentId
        site.parentType = 'customer'
        site.customerId = parentId
      }
    }
  }

  for (const project of projects) {
    if (project.parentLocIds && project.parentLocIds.length > 0) {
      const parentId = project.parentLocIds[0]

      // Check if parent is a customer
      const customerParent = customers.find(c => c.locationId === parentId)
      if (customerParent) {
        if (!customerParent.projects) customerParent.projects = []
        customerParent.projects.push(project)
        project.parentId = parentId
        project.parentType = 'customer'
        project.customerId = parentId
        continue
      }

      // Check if parent is a site
      const siteParent = sites.find(s => s.locationId === parentId)
      if (siteParent) {
        if (!siteParent.projects) siteParent.projects = []
        siteParent.projects.push(project)
        project.parentId = parentId
        project.parentType = 'site'
        project.siteId = parentId
      }
    }
  }

  // Link areas to projects or sites
  for (const area of areas) {
    if (area.parentLocIds && area.parentLocIds.length > 0) {
      const parentId = area.parentLocIds[0]

      // Check if parent is a project
      const projectParent = projects.find(p => p.locationId === parentId)
      if (projectParent) {
        if (!projectParent.areas) projectParent.areas = []
        projectParent.areas.push(area)
        area.parentId = parentId
        area.parentType = 'project'
        area.projectId = parentId
        continue
      }

      // Check if parent is a site
      const siteParent = sites.find(s => s.locationId === parentId)
      if (siteParent) {
        if (!siteParent.areas) siteParent.areas = []
        siteParent.areas.push(area)
        area.parentId = parentId
        area.parentType = 'site'
        area.siteId = parentId
      }
    }
  }

  return { customers }
}

/**
 * Flatten hierarchy back to a list with display information
 */
export function flattenHierarchy(hierarchy: LocationHierarchy): Array<Location & { level: number, displayName: string }> {
  const flattened: Array<Location & { level: number, displayName: string }> = []

  for (const customer of hierarchy.customers) {
    flattened.push({
      ...customer,
      level: 0,
      displayName: getDisplayDescription(customer.description) || customer.locationId
    })

    // Add sites
    if (customer.sites) {
      for (const site of customer.sites) {
        flattened.push({
          ...site,
          level: 1,
          displayName: getDisplayDescription(site.description) || site.locationId
        })

        // Add projects under site
        if (site.projects) {
          for (const project of site.projects) {
            flattened.push({
              ...project,
              level: 2,
              displayName: getDisplayDescription(project.description) || project.locationId
            })

            // Add areas under project
            if (project.areas) {
              for (const area of project.areas) {
                flattened.push({
                  ...area,
                  level: 3,
                  displayName: getDisplayDescription(area.description) || area.locationId
                })
              }
            }
          }
        }

        // Add areas under site
        if (site.areas) {
          for (const area of site.areas) {
            flattened.push({
              ...area,
              level: 2,
              displayName: getDisplayDescription(area.description) || area.locationId
            })
          }
        }
      }
    }

    // Add projects under customer
    if (customer.projects) {
      for (const project of customer.projects) {
        flattened.push({
          ...project,
          level: 1,
          displayName: getDisplayDescription(project.description) || project.locationId
        })

        // Add areas under project
        if (project.areas) {
          for (const area of project.areas) {
            flattened.push({
              ...area,
              level: 2,
              displayName: getDisplayDescription(area.description) || area.locationId
            })
          }
        }
      }
    }
  }

  return flattened
}
